// src/pages/ChatWindow.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { FiMessageSquare } from "react-icons/fi";
import { FaPlus, FaPaperclip,  FaPaperPlane, FaXmark } from "react-icons/fa6";
import * as api from "../../api/index";
import { IoMdArrowBack } from "react-icons/io";
import { io } from "socket.io-client";

const IMAGEKIT_BASE = "https://ik.imagekit.io/1h5qhnqzz";
const SOCKET_URL = "https://asap-backend-1.onrender.com" || "http://localhost:5000";

export default function ChatWindow() {
  const { conversationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const otherUserFromState = location.state?.otherUser || {
    Name: "OurAsap Service",
    profileImage: "/default.png",
    _id: "",
  };

  const token = localStorage.getItem("token");
  let decoded = null;
  try { decoded = token ? jwtDecode(token) : null; } catch (err) {}

  const userId = decoded?.id || decoded?._id || "me123";
  
  const [file, setFile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [preview, setPreview] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [text, setText] = useState("");

  const scrollRef = useRef(null);
  const socketRef = useRef(null);
  const inputRef = useRef(null);

  // --- SOCKET SETUP (LOGIC PRESERVED) ---
  useEffect(() => {
    if (!conversationId) return;
    socketRef.current = io(SOCKET_URL, { auth: { token }, transports: ["websocket"] });
    const socket = socketRef.current;
    socket.on("connect", () => { socket.emit("join_conversation", { conversationId }); });
    socket.on("new_message", (msg) => {
      if (!msg || msg.conversationId !== conversationId) return;
      setMessages((prev) => {
        const isDuplicate = prev.some(m => m._id === msg._id || (m.pending && m.text === msg.text && String(m.sender) === String(msg.sender)));
        if (isDuplicate) return prev;
        const map = new Map(prev.map((m) => [m._id, m]));
        map.set(msg._id, msg);
        return [...map.values()];
      });
      scrollToBottom();
    });
    socket.on("message_updated", (msg) => {
      setMessages((prev) => {
        const map = new Map(prev.map((m) => [m._id, m]));
        map.set(msg._id, msg);
        return [...map.values()];
      });
    });
    socket.on("message_deleted", ({ messageId }) => {
      setMessages((prev) => prev.filter((m) => m._id !== messageId));
    });
    socket.on("connect_error", () => { setError("Real-time connection failed. Retrying..."); });
    return () => {
      if (socket) {
        socket.emit("leave_conversation", { conversationId });
        socket.disconnect();
      }
    };
  }, [conversationId, token]);

  const scrollToBottom = () => {
    setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
  };

  // --- LOAD MESSAGES (LOGIC PRESERVED) ---
  useEffect(() => {
    const load = async () => {
      if (!conversationId) return;
      try {
        setLoading(true);
        setError(null);
        const res = await api.getConversationMessages(conversationId, userId, page, limit);
        if (res?.data?.messages) {
          setMessages((prev) => {
            const map = new Map(prev.map((m) => [m._id, m]));
            res.data.messages.forEach((m) => map.set(m._id, m));
            return [...map.values()];
          });
        }
        try { await api.markConversationRead(conversationId, userId); } catch {}
      } catch (err) {
        setError("Failed to load messages.");
      } finally {
        setLoading(false);
        scrollToBottom();
        if (inputRef.current) inputRef.current.focus();
      }
    };
    load();
  }, [conversationId, page]);

  // --- SEND MESSAGE (LOGIC PRESERVED) ---
  const handleSend = async (e) => {
    e?.preventDefault();
    if (!text.trim() && !file) return;
    if (file && !file.type.startsWith("image/")) { setError("Only image files allowed."); return; }
    setError(null);

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const tempMessage = { _id: tempId, sender: userId, text, type: file ? "image" : "text", previewFile: preview || null, createdAt: new Date().toISOString(), pending: true };

    setMessages((prev) => [...prev, tempMessage]);
    scrollToBottom();
    setText("");
    setFile(null);
    setPreview(null);

    try {
      const formData = new FormData();
      formData.append("text", tempMessage.text);
      formData.append("userId", userId);
      if (file) formData.append("file", file);
      const res = await api.postConversationMessage(conversationId, formData);
      if (res?.data?.message) {
        setMessages((prev) => prev.map((m) => (m._id === tempId ? res.data.message : m)));
      }
    } catch {
      setMessages((prev) => prev.filter((m) => m._id !== tempId));
      setError("Failed to send message");
    }
  };

  const renderMessageContent = (msg) => {
    if (msg.previewFile) return <img src={msg.previewFile} alt="preview" className="max-w-[220px] rounded-xl object-cover mb-1" />;
    if (msg.type === "image" && msg.file) {
      const url = msg.file.startsWith("http") ? msg.file : `${IMAGEKIT_BASE}/${msg.file}`;
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img src={url} alt="sent" className="max-w-[220px] rounded-xl object-cover cursor-pointer mb-1 hover:opacity-90 transition" />
        </a>
      );
    }
    if (msg.text && (!msg.type || msg.type === "text")) return <div className="leading-relaxed">{msg.text}</div>;
    return null;
  };

  return (
    <div className="flex flex-col h-screen bg-[#F3F4F6]">
      {/* 🔝 Premium Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 p-3 px-4 flex items-center shadow-sm">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition text-gray-700">
          <IoMdArrowBack size={24} />
        </button>
        <div className="flex items-center ml-2 flex-1">
          <div className="relative">
            <img
              src={otherUserFromState?.profileImage || "https://res.cloudinary.com/dvuq6vmiy/image/upload/v1758705055/istockphoto-1288129985-612x612_hhrco1.jpg"}
              alt="profile"
              className="w-10 h-10 rounded-2xl object-cover border-2 border-orange-100"
            />
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
          </div>
          <div className="ml-3 overflow-hidden">
            <h2 className="font-black text-sm text-gray-900 uppercase tracking-tight truncate">{otherUserFromState?.Name || "Chat"}</h2>
            <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest">Online</p>
          </div>
        </div>
      </div>

      {/* ⚠️ Error Banner */}
      {error && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-red-500 text-white text-[10px] font-black uppercase tracking-widest text-center py-2 animate-pulse">
          {error}
        </div>
      )}

      {/* 💬 Chat Area */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 pt-20 pb-24 space-y-4 scroll-smooth bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"
      >
        <div className="flex justify-center mb-6">
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={loading}
            className="text-[10px] font-black uppercase tracking-widest px-4 py-2 bg-white/50 backdrop-blur shadow-sm rounded-full text-gray-500 hover:text-orange-600 transition disabled:opacity-30"
          >
            {loading ? "Syncing..." : "Load History"}
          </button>
        </div>

        {messages.length === 0 && !loading && (
          <div className="text-center py-10 opacity-40">
            <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-[2rem] flex items-center justify-center mx-auto mb-4">
              <FiMessageSquare size={30} />
            </div>
            <p className="font-black text-gray-400 uppercase tracking-widest text-xs">How could we assist you?👋</p>
          </div>
        )}

        {[...new Map(messages.map((m) => [m._id, m])).values()].map((msg) => {
          const mine = msg.sender?._id === userId || msg.sender === userId;
          return (
            <div key={msg._id || `${msg.sender}-${msg.createdAt}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`group relative max-w-[80%] px-4 py-3 rounded-[1.5rem] shadow-sm transition-all ${
                mine ? "bg-orange-600 text-white rounded-tr-none" : "bg-white text-gray-900 rounded-tl-none border border-gray-100"
              }`}>
                {renderMessageContent(msg)}
                <div className={`text-[9px] font-black uppercase tracking-tighter mt-1 opacity-70 ${mine ? "text-right" : "text-left"}`}>
                  {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                  {msg.pending && " • Sending..."}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 📎 Image Preview Overlay */}
      {file && (
        <div className="fixed bottom-20 left-4 z-50 animate-bounce-in">
          <div className="bg-white p-2 rounded-2xl shadow-2xl border-2 border-orange-500 flex items-center gap-3">
            <img src={preview} alt="preview" className="w-14 h-14 object-cover rounded-xl" />
            <button 
              onClick={() => { setFile(null); setPreview(null); }}
              className="bg-red-100 text-red-600 p-2 rounded-xl hover:bg-red-200 transition"
            >
              <FaXmark size={18} />
            </button>
          </div>
        </div>
      )}

      {/* ⌨️ Composer Section */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-gray-100 p-4 z-50">
        <form onSubmit={handleSend} className="max-w-4xl mx-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/create-order", { state: { sellerId: otherUserFromState._id || otherUserFromState.id } })}
            className="w-12 h-12 bg-gray-900 text-white rounded-2xl flex items-center justify-center hover:bg-orange-600 transition-colors shadow-lg active:scale-95 shrink-0"
          >
            <FaPlus size={18} />
          </button>

          <div className="relative flex-1">
            <input
              ref={inputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Start typing..."
              className="w-full bg-gray-100 border-2 border-transparent focus:border-orange-500 focus:bg-white rounded-2xl py-3.5 pl-5 pr-12 font-bold text-sm text-gray-900 transition-all outline-none"
            />
            <button
              type="button"
              onClick={() => document.getElementById("fileInput").click()}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-600 p-2"
            >
              <FaPaperclip size={18} />
            </button>
          </div>

          <button
            type="submit"
            disabled={!text.trim() && !file}
            className="w-12 h-12 bg-orange-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-orange-200 disabled:opacity-30 disabled:shadow-none active:scale-95 transition-all shrink-0"
          >
            <FaPaperPlane size={18} />
          </button>

          <input
            type="file"
            accept="image/*"
            id="fileInput"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files[0];
              setFile(f);
              if (f) {
                const reader = new FileReader();
                reader.onloadend = () => setPreview(reader.result);
                reader.readAsDataURL(f);
              }
            }}
          />
        </form>
      </div>
    </div>
  );
}